import type { DroneRegime, DronesParams, Exercise, ExerciseParameters, NamedPlace, Phase, ZoneLetter } from "../api/types";
import { lang as interfaceLanguage } from "../i18n/i18n";
import { isZoneLetter, ZONE_OFFSET_H } from "../map/dtg";
import { licenceStorage } from "../state/licenceScope";
import type { DeploymentParams } from "../state/ui";
import { SEASONAL, weatherKey, weatherState } from "../weather";

/** Brouillon des Données initiales : ce que montre le formulaire, enregistré ou non. Il vit dans la vue et, tant
 *  qu'il diffère du dossier, sur ce poste (clé par dossier, « new » pour un nouveau dossier), avec la version du
 *  dossier dont il part (`base`). Un brouillon n'est jamais jeté en silence (audit du 03/10/2026) : quand le
 *  dossier change de version sous lui (autre poste, déplacement sur la carte, faction d'un ODB ennemi, refus 412 à
 *  l'enregistrement), il est reporté champ par champ sur la nouvelle version (`rebaseDraft`). Le camp affiché sur la
 *  carte s'en déduit aussi (`displayedDossier`) : jamais le décor d'un camp avec les unités d'un autre. */
export interface Draft {
  name: string;
  /** Langue de l'ordre : celle de l'interface du poste (décision d'Alexis du 24/10/2026, docs/13 « Langue des
   *  documents ») ; les documents la suivent à la lecture. Jamais un choix du formulaire, jamais un écart. */
  language: "fr" | "en";
  mapSlug: string | null;
  /** « Nouveau terrain » choisi sous la carte « Terrain libre » (demande d'Alexis du 03/10/2026) : à
   *  l'enregistrement, le dossier est enregistré sans carte (un dossier déjà posé garde la sienne jusqu'à la
   *  préparation du nouveau terrain), puis la vue Manœuvre s'ouvre pour placer le terrain, le fuseau et les LIMA.
   *  Durable avec le lieu nommé (`parameters.place`) : un dossier enregistré qui attend son terrain le garde coché. */
  freeTerrain: boolean;
  parameters: ExerciseParameters;
}

export const EMPTY_PARAMETERS: ExerciseParameters = {
  level: "REGIMENT",
  odb: null,
  mode_action: null,
  mission: null,
  posture: null,
  enemy_type: "Titan",
  start_dtg: null,
  time_zone: "B",
  weather_scenario: "auto",
  weather_custom: null,
  robotized_ami: false,
  robotized_eni: false,
  population: "Amicale",
  strategic_site: false,
  militia: false,
  centac_rotation_deg: null,
  centac_rotation: null,
  eni_odb: null,
  enemy_profile: null,
  custom_enemy: null,
  drones: null,
  extra: {},
};

/** Réglages drones sous leur forme du brouillon (chantier « Drones ») : sous-clés vides retirées (la réponse du
 *  dossier les montre à null, l'enregistrement les omet) ; plus rien : null, tout est déduit. Deux formes du même
 *  réglage se comparent donc égales : aucun faux « modifié ». */
export function dronesParamsOf(params: DronesParams | null | undefined): DronesParams | null {
  if (!params || typeof params !== "object") return null;
  const out: DronesParams = {};
  const regime = Object.fromEntries(Object.entries(params.regime ?? {}).filter(([, v]) => v)) as Partial<Record<Phase, DroneRegime>>;
  if (Object.keys(regime).length) out.regime = regime;
  if (params.vaa_ceiling_m != null) out.vaa_ceiling_m = params.vaa_ceiling_m;
  if (params.mto_priorities?.length) out.mto_priorities = params.mto_priorities;
  if (params.jamming_phase) out.jamming_phase = params.jamming_phase;
  if (params.thermal != null) out.thermal = params.thermal;
  return Object.keys(out).length ? out : null;
}

type Dossier = Pick<Exercise, "id" | "version" | "name" | "language" | "map_slug" | "parameters">;

/** Début rangé avec un fuseau (dossier décliné d'un chef sans date : « 2026-10-30T14:00:00Z ») ramené à la forme des
 *  Données initiales : l'heure locale du fuseau du dossier, sans fuseau (Z sans fuseau du dossier), comme l'OPO la
 *  lit. Le champ montre alors l'heure des GDH, et une retouche ne décale plus le début ; l'instant ne change pas. Un
 *  début sans fuseau est déjà sous cette forme. */
export function localStartDtg(start: string | null, letter: ZoneLetter | null | undefined): string | null {
  if (!start || !/(Z|[+-]\d{2}:?\d{2})$/i.test(start)) return start;
  const instant = Date.parse(start);
  if (!Number.isFinite(instant)) return start;
  const offset = ZONE_OFFSET_H[letter && isZoneLetter(letter) ? letter : "Z"];
  return new Date(instant + offset * 3_600_000).toISOString().slice(0, 19);
}

/** Paramètres avec ou sans lieu nommé : la clé est omise sans lieu (comme le serveur la range), jamais null — deux
 *  formes du même « aucun lieu » se comparent égales. */
export function withPlace(params: ExerciseParameters, place: NamedPlace | null | undefined): ExerciseParameters {
  const { place: _dropped, ...rest } = params;
  return place ? { ...rest, place } : rest;
}

/** État du dossier enregistré, sous la forme du brouillon. Un lieu nommé (hors CENTAC) dit que le dossier attend son
 *  nouveau terrain : « Nouveau terrain » reste coché. */
export function fromExercise(ex: Dossier | undefined): Draft {
  // Langue : celle de l'interface (les documents la suivent, docs/13), pour un nouveau dossier comme pour un dossier
  // enregistré.
  if (!ex) return { name: "", language: interfaceLanguage(), mapSlug: null, freeTerrain: false, parameters: { ...EMPTY_PARAMETERS } };
  const place = ex.parameters.level === "CENTAC" ? null : (ex.parameters.place ?? null);
  const parameters = withPlace({ ...EMPTY_PARAMETERS, ...ex.parameters, drones: dronesParamsOf(ex.parameters.drones) }, place);
  const start = localStartDtg(parameters.start_dtg, parameters.time_zone);
  return {
    name: ex.name,
    language: interfaceLanguage(),
    mapSlug: ex.map_slug,
    freeTerrain: Boolean(place),
    parameters: start === parameters.start_dtg ? parameters : { ...parameters, start_dtg: start },
  };
}

/** Météo du brouillon remise sur « Saisonnière » (nouveau terrain à placer, passage en CENTAC) : `changed` dit s'il y
 *  avait une météo choisie ou importée, pour l'annoncer. Une météo déjà saisonnière n'est pas touchée. */
export function withSeasonalWeather(d: Draft): { draft: Draft; changed: boolean } {
  if (weatherState(d.parameters) === "seasonal") return { draft: d, changed: false };
  return { draft: { ...d, parameters: { ...d.parameters, weather_scenario: SEASONAL, weather_custom: null } }, changed: true };
}

// --- Comparaison ----------------------------------------------------------------------------------------------

/** Forme canonique (clés triées, valeurs `undefined` omises) : deux brouillons égaux se comparent égaux quel que
 *  soit l'ordre de leurs clés. */
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = canonical(v);
    }
    return out;
  }
  return value;
}

export function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

/** Le brouillon diffère-t-il du dossier enregistré (ou d'un nouveau dossier vide) ? */
export function isModified(draft: Draft, ex: Dossier | undefined): boolean {
  return !sameValue({ ...draft, language: interfaceLanguage() }, fromExercise(ex));
}

/** Modifié autrement que par le choix « Terrain libre » : sinon rien à écrire, ni à prévisualiser. Le lieu nommé du
 *  nouveau terrain, lui, s'écrit (« Enregistrer et placer le terrain »). */
export function modifiedWithoutChoice(draft: Draft, ex: Dossier | undefined): boolean {
  return !sameValue({ ...draft, freeTerrain: false, language: interfaceLanguage() }, { ...fromExercise(ex), freeTerrain: false });
}

// --- Fusion champ par champ -----------------------------------------------------------------------------------

/** Champ du brouillon : nom, langue, carte, choix « Terrain libre », ou un paramètre (`parameters.mission`). */
export type FieldKey = "name" | "language" | "mapSlug" | "freeTerrain" | `parameters.${string}`;

/** Champs qui ne se reportent qu'ensemble : carte, choix « Terrain libre » et lieu nommé du nouveau terrain ; météo
 *  et son choix. */
const LINKED: readonly (readonly FieldKey[])[] = [
  ["mapSlug", "freeTerrain", "parameters.place"],
  ["parameters.weather_scenario", "parameters.weather_custom"],
];

/** Niveau, force, mission (avec son mode d'action) et ODB ennemi : chacun dépend de son parent (la force et l'ODB
 *  ennemi du niveau, la mission de la force). Un champ ne se reporte qu'avec ses parents s'ils diffèrent (jamais la
 *  mission d'une autre force) et, s'il change, avec ses enfants ; un enfant seul (la mission à force égale, l'ODB
 *  ennemi à niveau égal) se reporte seul (`chainClosure`). */
const CHAIN: readonly { keys: readonly FieldKey[]; parent: number | null }[] = [
  { keys: ["parameters.level"], parent: null },
  { keys: ["parameters.odb"], parent: 0 },
  { keys: ["parameters.mission", "parameters.mode_action", "parameters.posture"], parent: 1 },
  { keys: ["parameters.eni_odb"], parent: 0 },
];

const chainUnitOf = (key: FieldKey) => CHAIN.findIndex((unit) => unit.keys.includes(key));
const unitKeys = (unit: number): readonly FieldKey[] => CHAIN[unit]?.keys ?? [];

function unitDiffers(a: Draft, b: Draft, unit: number): boolean {
  return unitKeys(unit).some((key) => !sameValue(fieldValue(a, key), fieldValue(b, key)));
}

/** Éléments de la chaîne à prendre de `source` pour `target` à partir de `seeds` : avec chacun, ses parents qui
 *  diffèrent et, s'il change, ses enfants, jusqu'à un ensemble cohérent. */
function chainClosure(target: Draft, source: Draft, seeds: Iterable<number>): Set<number> {
  const taken = new Set(seeds);
  let grew = true;
  const add = (unit: number) => {
    if (taken.has(unit)) return;
    taken.add(unit);
    grew = true;
  };
  while (grew) {
    grew = false;
    for (const unit of [...taken]) {
      const parent = CHAIN[unit]?.parent ?? null;
      if (parent !== null && unitDiffers(target, source, parent)) add(parent);
      if (unitDiffers(target, source, unit)) CHAIN.forEach((child, i) => child.parent === unit && add(i));
    }
  }
  return taken;
}

export function fieldValue(draft: Draft, key: FieldKey): unknown {
  if (key.startsWith("parameters.")) return (draft.parameters as unknown as Record<string, unknown>)[key.slice(11)];
  return draft[key as "name" | "language" | "mapSlug" | "freeTerrain"];
}

function withField(draft: Draft, key: FieldKey, value: unknown): Draft {
  if (key === "parameters.place") return { ...draft, parameters: withPlace(draft.parameters, value as NamedPlace | null | undefined) };
  if (key.startsWith("parameters.")) return { ...draft, parameters: { ...draft.parameters, [key.slice(11)]: value } };
  return { ...draft, [key]: value };
}

/** Groupe de report d'un champ hors de la chaîne niveau → force → mission (lui seul, ou ses champs liés). */
function linkedFields(key: FieldKey): readonly FieldKey[] {
  return LINKED.find((group) => group.includes(key)) ?? [key];
}

/** Champs pris de `source` pour `target` quand `fields` le sont : leurs champs liés et, pour la chaîne niveau →
 *  force → mission, ce qui va avec eux (`chainClosure`). */
function takenWith(target: Draft, source: Draft, fields: readonly FieldKey[]): FieldKey[] {
  const seeds = fields.map(chainUnitOf).filter((unit) => unit >= 0);
  const chain = [...chainClosure(target, source, seeds)].flatMap(unitKeys);
  return [...new Set([...fields.filter((key) => chainUnitOf(key) < 0).flatMap(linkedFields), ...chain])];
}

/** Tous les groupes de report des brouillons donnés hors de la chaîne : champs liés, puis chacun des autres. */
function groupsOf(...drafts: Draft[]): (readonly FieldKey[])[] {
  const linked = new Set([...LINKED.flat(), ...CHAIN.flatMap((unit) => unit.keys)]);
  // La langue n'est jamais reportée : elle suit l'interface (docs/13).
  const singles = new Set<FieldKey>(["name"]);
  for (const draft of drafts)
    for (const name of Object.keys(draft.parameters)) {
      const key: FieldKey = `parameters.${name}`;
      if (!linked.has(key)) singles.add(key);
    }
  return [...LINKED, ...[...singles].map((key) => [key])];
}

/** Champs modifiés des deux côtés : champ → valeur saisie au moment du report. Le champ reste signalé tant que la
 *  saisie garde cette valeur et que le dossier en a une autre (`openConflicts`). */
export type Conflicts = Partial<Record<FieldKey, unknown>>;

/** Report d'un brouillon sur une nouvelle version du dossier. `mine` : la saisie ; `base` : le dossier dont elle
 *  part (null pour un brouillon gardé avant ce report : on ne sait pas qui a changé quoi, tout ce qui diffère du
 *  dossier est gardé et signalé) ; `server` : le dossier tel qu'il est maintenant. Groupe par groupe : non touché
 *  par l'utilisateur, il suit le dossier ; touché, la saisie l'emporte ; touché des deux côtés avec des valeurs
 *  différentes, la saisie l'emporte et le champ est signalé. Niveau, force, mission et ODB ennemi : seuls les champs
 *  saisis et ce qui va avec eux l'emportent (`chainClosure`), le reste suit le dossier. Un « Nouveau terrain » demandé
 *  est servi quand le dossier a reçu depuis un terrain libre ; sinon il est gardé, sur la carte actuelle du dossier,
 *  et signalé si le dossier a changé de carte. Les conflits déjà signalés et toujours ouverts sont gardés. */
export function rebaseDraft(mine: Draft, base: Draft | null, server: Draft, previous: Conflicts = {}): { draft: Draft; conflicts: Conflicts } {
  let draft = server;
  const conflicts: Conflicts = {};
  const reference = base ?? server;
  const touched = (keys: readonly FieldKey[]) =>
    keys.some((key) => !sameValue(fieldValue(mine, key), fieldValue(reference, key)));
  const serverMoved = (keys: readonly FieldKey[]) =>
    base === null || keys.some((key) => !sameValue(fieldValue(server, key), fieldValue(base, key)));
  const keep = (keys: readonly FieldKey[], moved: boolean) => {
    for (const key of keys) {
      const value = fieldValue(mine, key);
      draft = withField(draft, key, value);
      if (moved && !sameValue(value, fieldValue(server, key))) conflicts[key] = value;
    }
  };
  for (const group of groupsOf(mine, reference, server)) {
    if (!touched(group)) continue;
    if (group.includes("freeTerrain") && mine.freeTerrain) {
      // Carte affichée en attendant le nouveau terrain : celle du dossier, toujours (`chooseMap`).
      const before = (base ?? mine).mapSlug;
      const mapMoved = !sameValue(server.mapSlug, before);
      if (mapMoved && server.mapSlug?.startsWith("zone-")) continue; // terrain libre reçu depuis : demande servie
      draft = { ...draft, freeTerrain: true, parameters: withPlace(draft.parameters, mine.parameters.place) };
      if (mapMoved) conflicts.freeTerrain = true;
      continue;
    }
    keep(group, serverMoved(group));
  }
  const seeds = CHAIN.map((_, unit) => unit).filter((unit) => touched(unitKeys(unit)));
  // Ce qui l'emporte va ensemble : si le dossier a changé l'un de ces champs, chacun de ceux qui diffèrent est signalé.
  const chain = [...chainClosure(server, mine, seeds)].flatMap(unitKeys);
  if (chain.length) keep(chain, serverMoved(chain));
  for (const [key, value] of Object.entries(previous) as [FieldKey, unknown][])
    if (!(key in conflicts) && sameValue(fieldValue(draft, key), value) && !sameValue(fieldValue(server, key), value))
      conflicts[key] = value;
  return { draft, conflicts };
}

/** Champs encore signalés : la saisie garde la valeur du report et le dossier en a une autre. */
export function openConflicts(conflicts: Conflicts, draft: Draft, server: Draft): Set<FieldKey> {
  const open = new Set<FieldKey>();
  for (const [key, value] of Object.entries(conflicts) as [FieldKey, unknown][])
    if (sameValue(fieldValue(draft, key), value) && !sameValue(fieldValue(server, key), value)) open.add(key);
  return open;
}

/** « Reprendre la valeur enregistrée » (ou report d'un ordre reçu) : les champs reprennent la valeur du dossier,
 *  avec leurs champs liés et ce qui va avec eux dans la chaîne niveau → force → mission (`takenWith`). */
export function takeServerFields(draft: Draft, server: Draft, ...keys: FieldKey[]): Draft {
  return takenWith(draft, server, keys).reduce((d, k) => withField(d, k, fieldValue(server, k)), draft);
}

// --- Copie sur ce poste ---------------------------------------------------------------------------------------

export const DRAFT_PREFIX = "generex-draft:";

export function draftKey(exerciseId: string | null | undefined): string {
  return `${DRAFT_PREFIX}${exerciseId ?? "new"}`;
}

/** Brouillon gardé sur ce poste : la saisie, le dossier dont elle part et sa version, les champs signalés, et si
 *  elle a déjà été reportée sur une version plus récente (l'avis reste affiché après un rechargement). */
export interface StoredDraft {
  draft: Draft;
  baseVersion: number;
  /** Absent d'un brouillon gardé avant le report champ par champ. */
  base?: Draft;
  conflicts?: Conflicts;
  rebased?: boolean;
  savedAt?: number;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Stockage du navigateur rangé sous la licence en cours (audit de persistance M8 : `state/licenceScope`),
 *  protégé : certains contextes (stockage bloqué, aperçus, tests) lèvent une exception. */
function browserStore(): Store | null {
  return typeof window === "undefined" ? null : licenceStorage;
}

export function readStoredDraft(key: string, store: Store | null = browserStore()): StoredDraft | null {
  try {
    const raw = store?.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredDraft>;
    return stored.draft && typeof stored.draft === "object" && typeof stored.baseVersion === "number" ? (stored as StoredDraft) : null;
  } catch {
    return null;
  }
}

export function writeStoredDraft(key: string, entry: StoredDraft, store: Store | null = browserStore()): void {
  try {
    store?.setItem(key, JSON.stringify({ ...entry, savedAt: Date.now() }));
  } catch {
    /* stockage indisponible : brouillon non gardé */
  }
}

export function removeStoredDraft(key: string, store: Store | null = browserStore()): void {
  try {
    store?.removeItem(key);
  } catch {
    /* idem */
  }
}

/** Brouillon d'une version antérieure de l'écran : les champs qui lui manquent viennent du dossier. */
function completed(partial: Draft, server: Draft): Draft {
  return { ...server, ...partial, parameters: { ...server.parameters, ...partial.parameters } };
}

/** État de saisie des Données initiales : brouillon, dossier dont il part (et sa version), champs signalés, report
 *  sur une version plus récente à signaler. */
export interface DraftState {
  draft: Draft;
  base: Draft;
  baseVersion: number;
  conflicts: Conflicts;
  rebased: boolean;
}

export function freshDraftState(ex: Dossier | undefined): DraftState {
  const server = fromExercise(ex);
  return { draft: server, base: server, baseVersion: ex?.version ?? 0, conflicts: {}, rebased: false };
}

/** État de saisie à l'ouverture des Données initiales (ou pour la carte, `storedDraftFor`) : le brouillon gardé sur
 *  ce poste, reporté sur la version actuelle du dossier s'il part d'une autre ; sinon le dossier. */
export function loadDraft(ex: Dossier | undefined, store: Store | null = browserStore()): DraftState {
  const stored = readStoredDraft(draftKey(ex?.id), store);
  if (!stored) return freshDraftState(ex);
  const server = fromExercise(ex);
  const version = ex?.version ?? 0;
  const mine = completed(stored.draft, server);
  if (stored.baseVersion === version)
    return { draft: mine, base: server, baseVersion: version, conflicts: stored.conflicts ?? {}, rebased: Boolean(stored.rebased) };
  const { draft, conflicts } = rebaseDraft(mine, stored.base ? completed(stored.base, server) : null, server, stored.conflicts);
  return { draft, base: server, baseVersion: version, conflicts, rebased: !sameValue(draft, server) };
}

/** Force par défaut (la première de la liste du niveau `listLevel`), décidée sur le brouillon au moment où la mise à
 *  jour s'applique : un brouillon qui a déjà une force (celui du dossier, adopté dans le même rendu) ou d'un autre
 *  niveau que la liste n'est pas touché. */
export function withDefaultForce(d: Draft, ref: string, listLevel: string): Draft {
  if (d.parameters.odb || (d.parameters.level ?? "REGIMENT") !== listLevel) return d;
  return { ...d, parameters: { ...d.parameters, odb: ref } };
}

/** Ennemi du brouillon d'après la force choisie (registre B39), comme `normalize_parameters` du serveur, pour
 *  qu'aucun faux « modifié » n'apparaisse : une force non française (`neutral`) affronte l'ennemi générique
 *  (`enemy_type` vide) ; revenue à une force française (`wasNeutral`) sans ennemi, « Titan » est remis. CENTAC
 *  impose déjà son ennemi. */
export function withForceEnemy(d: Draft, neutral: boolean, wasNeutral: boolean): Draft {
  if (d.parameters.level === "CENTAC") return d;
  if (neutral && d.parameters.enemy_type !== null) return { ...d, parameters: { ...d.parameters, enemy_type: null } };
  if (!neutral && wasNeutral && d.parameters.enemy_type === null)
    return { ...d, parameters: { ...d.parameters, enemy_type: "Titan" } };
  return d;
}

/** Mission résolue (`mission`, `mode`) d'après la liste de la force `seen.odb`, quand la mission vue était `seen.mission` :
 *  rien si le brouillon a changé de force ou de mission entre-temps (dossier adopté, autre choix). */
export function withResolvedMission(
  d: Draft,
  seen: { odb: string | null; mission: string | null },
  mission: string,
  mode: string | null,
  posture?: ExerciseParameters["posture"],
): Draft {
  if (d.parameters.odb !== seen.odb || d.parameters.mission !== seen.mission) return d;
  return { ...d, parameters: { ...d.parameters, mission, mode_action: mode, ...(posture !== undefined ? { posture } : {}) } };
}

/** Brouillon gardé sur ce poste qui modifie le dossier enregistré (reporté sur sa version actuelle), sinon null. */
export function storedDraftFor(ex: Dossier, store: Store | null = browserStore()): Draft | null {
  const { draft } = loadDraft(ex, store);
  return isModified(draft, ex) ? draft : null;
}

/** Report d'un ordre reçu dans les Données initiales (choix explicite de l'utilisateur) : le brouillon gardé est
 *  reporté sur le dossier mis à jour ; les champs reportés (`reported`, avec ce qui va avec eux : la mission d'une
 *  force reportée, l'ODB ennemi d'un niveau reporté) prennent la valeur du dossier, le reste de la saisie est gardé. */
export function rebaseStoredDraft(ex: Dossier, reported: readonly FieldKey[], store: Store | null = browserStore()): void {
  const key = draftKey(ex.id);
  if (!readStoredDraft(key, store)) return;
  const state = loadDraft(ex, store);
  const server = fromExercise(ex);
  const draft = takeServerFields(state.draft, server, ...reported);
  const dropped = new Set(takenWith(state.draft, server, reported));
  const conflicts = Object.fromEntries(Object.entries(state.conflicts).filter(([field]) => !dropped.has(field as FieldKey))) as Conflicts;
  if (sameValue(draft, server)) removeStoredDraft(key, store);
  else writeStoredDraft(key, { draft, base: server, baseVersion: ex.version, conflicts, rebased: true }, store);
}

// --- Dispositif et camp affichés ------------------------------------------------------------------------------

/** Force et mission résolues : le dispositif du brouillon peut être calculé (pas de calcul intermédiaire pendant un
 *  changement de niveau). */
export function draftReady(draft: Draft): boolean {
  return Boolean(draft.parameters.odb) && (draft.parameters.level === "CENTAC" || Boolean(draft.parameters.mission));
}

/** Paramètres du dispositif affiché pour un brouillon : celui du dossier enregistré s'il n'est pas modifié (seul le
 *  choix « Terrain libre » peut différer), sinon l'aperçu du brouillon, qui part de l'état du dossier (`base` :
 *  déplacements, fuseau personnalisé, missions choisies, conservés selon les règles du changement des Données
 *  initiales). null : pas de camp, rien à afficher. Les Données initiales et la carte (hors de cette vue) en tirent
 *  les mêmes paramètres : passer de l'une à l'autre ne recalcule rien. */
export function deploymentParamsOf(draft: Draft, current: Dossier | undefined): DeploymentParams | null {
  const p = draft.parameters;
  if (!draft.mapSlug || !p.level) return null;
  const centac = p.level === "CENTAC";
  const preview = Boolean(current) && modifiedWithoutChoice(draft, current);
  return {
    exercise_id: current && !preview ? current.id : null,
    base: current && preview ? current.id : null,
    level: p.level,
    map_slug: draft.mapSlug,
    odb: p.odb,
    mission: p.mission,
    enemy_type: p.enemy_type,
    start_dtg: p.start_dtg,
    time_zone: p.time_zone,
    rotation_deg: centac ? (p.centac_rotation_deg ?? 0) : null,
    // Ordre de bataille ennemi : clé absente sans lui (aperçu et cache inchangés).
    ...(!centac && p.eni_odb ? { eni_odb: p.eni_odb } : {}),
    // Langue du brouillon : toponymes d'une zone libre (noms anglais en anglais), unités de l'ODB comprises.
    language: draft.language,
  };
}

/** Camp et dispositif affichés hors des Données initiales : ceux du brouillon gardé sur ce poste s'il modifie le
 *  dossier (les écritures de la carte restent alors bloquées, « Enregistrer » est désigné), sinon ceux du dossier
 *  enregistré. Toujours le même camp pour le décor et pour les unités. Brouillon dont la force ou la mission ne sont
 *  pas encore résolues : son camp, sans unités (`params` null), jusqu'au retour dans les Données initiales. `weather` :
 *  la météo du même dossier affiché (« auto » en CENTAC), envoyée par la mesure et l'analyse du terrain. */
export function displayedDossier(
  current: Dossier,
  draft: Draft | null,
): { mapSlug: string | null; params: DeploymentParams | null; weather: string | null } {
  const shown = draft ?? fromExercise(current);
  return {
    mapSlug: shown.mapSlug,
    params: draft && !draftReady(draft) ? null : deploymentParamsOf(shown, current),
    weather: weatherKey(shown.parameters),
  };
}
